// Transform the legacy Slate-shaped one-pager value into a Plate v53 value.
// Source shape comes from src/common/TextEditor/types/slate.d.ts.
// Target shape matches the plugins registered in usePlateEditorLogic.tsx.
//
// What this DOES NOT cover (intentionally simple per request):
//   - code blocks
// Unknown / unsupported nodes fall back to a plain paragraph so nothing
// disappears silently.

// Detect whether a saved value is still in the legacy Slate shape. Used at
// load time so the editor only runs the converter on un-migrated documents
// and lets already-Plate-shape JSON pass through untouched.
//
// Heuristic: search the tree (capped depth) for any type that ONLY exists
// in the Slate vocabulary — `paragraph`, `headingOne`/Two/Three/etc.,
// `unorderedList`/`orderedList`/`list-item`, `table-row`/`table-cell`/
// `table-header`. Presence of any of these means the doc is Slate-shape.
// Absence means the doc is either Plate-shape (uses `p` / `h1` / `tr` /
// `td` / `th`) or empty.
const SLATE_ONLY_TYPES = new Set([
    'paragraph',
    'headingOne',
    'headingTwo',
    'headingThree',
    'headingFour',
    'headingFive',
    'headingSix',
    'unorderedList',
    'orderedList',
    'bulleted-list',
    'numbered-list',
    'list-item',
    'table-row',
    'table-cell',
    'table-header',
    'tableWithoutHeaders',
]);

export function isSlateShape(value: any): boolean {
    if (!Array.isArray(value) || value.length === 0) return false;
    const stack: any[] = [...value];
    let scanned = 0;
    while (stack.length && scanned < 5000) {
        const n = stack.pop();
        scanned++;
        if (!n || typeof n !== 'object') continue;
        if (typeof n.type === 'string' && SLATE_ONLY_TYPES.has(n.type)) return true;
        if (Array.isArray(n.children)) stack.push(...n.children);
    }
    return false;
}

const ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify']);
const pickAlign = (n: any): string | undefined => {
    const a = n?.align ?? n?.textAlign;
    return typeof a === 'string' && ALIGN_VALUES.has(a) ? a : undefined;
};

const HEADING_MAP: Record<string, string> = {
    headingOne: 'h1',
    headingTwo: 'h2',
    headingThree: 'h3',
    headingFour: 'h3', // collapse to h3 (we only register h1-h3 in Plate)
    headingFive: 'h3',
    headingSix: 'h3',
};

const isTextNode = (n: any) => n && typeof n === 'object' && typeof n.text === 'string';

const wrapAsParagraph = (children: any[]): any => ({ type: 'p', children: children.length ? children : [{ text: '' }] });

const cleanTextNode = (n: any): any => {
    const {
        text,
        bold,
        italic,
        underline,
        strikethrough,
        code,
        color,
        bgColor,
        backgroundColor,
        fontSize,
        fontFamily,
        superscript,
        subscript,
        highlight,
        kbd,
        ...rest
    } = n;
    const out: any = { text: text ?? '' };
    if (bold) out.bold = true;
    if (italic) out.italic = true;
    if (underline) out.underline = true;
    if (strikethrough) out.strikethrough = true;
    if (code) out.code = true;
    if (color) out.color = color;
    const bg = backgroundColor ?? bgColor;
    if (bg) out.backgroundColor = bg;
    if (fontSize) out.fontSize = fontSize;
    if (fontFamily) out.fontFamily = fontFamily;
    if (superscript) out.superscript = true;
    if (subscript) out.subscript = true;
    // Highlight + kbd marks (Plate basic-nodes). Highlight is a boolean
    // (or color) flag set by HighlightPlugin; kbd is a boolean from
    // KbdPlugin. Round-trip both verbatim.
    if (highlight) out.highlight = highlight;
    if (kbd) out.kbd = true;
    // forward any other custom props (defensive)
    Object.assign(out, rest);
    return out;
};

// Promote a legacy Slate mention text node (carrying mention: true +
// mentionUserId on a text leaf) into a Plate inline mention element so the
// chip renders correctly and round-trips on save.
const convertMentionTextNode = (n: any): any => {
    const userId = n.mentionUserId ?? n.userId ?? '';
    const name = n.mentionUserName ?? (typeof n.text === 'string' ? n.text.replace(/^@/, '') : '');
    const value = typeof n.text === 'string' && n.text.startsWith('@') ? n.text : `@${name}`;
    return {
        type: 'mention',
        key: String(userId),
        value,
        children: [{ text: '' }],
    };
};

const convertChildren = (children: any[]): any[] => {
    if (!Array.isArray(children)) return [{ text: '' }];
    const out: any[] = [];
    for (const c of children) {
        if (isTextNode(c) && c.mention && (c.mentionUserId ?? c.userId)) {
            // Slate stores mentions as text leaves; Plate needs inline elements.
            // Slate's inline-element contract requires void elements to sit
            // between text nodes — surround the chip with empty text leaves.
            out.push({ text: '' }, convertMentionTextNode(c), { text: '' });
            continue;
        }
        const converted = convertNode(c);
        if (Array.isArray(converted)) out.push(...converted);
        else if (converted) out.push(converted);
    }
    return out.length ? out : [{ text: '' }];
};

// Wrap text-only / inline children inside a paragraph (Plate cells need a
// block child).
const ensureBlockChildren = (children: any[]): any[] => {
    if (!Array.isArray(children) || children.length === 0) return [wrapAsParagraph([{ text: '' }])];
    const allInline = children.every((c) => isTextNode(c) || (c && (c.type === 'link' || c.type === 'a' || typeof c.type === 'undefined')));
    if (allInline) return [wrapAsParagraph(children.map(cleanTextNode))];
    return children.map((c) => {
        if (isTextNode(c)) return wrapAsParagraph([cleanTextNode(c)]);
        return c;
    });
};

// Recursive list flattener. Slate stores nested lists as
//   <bulleted-list>
//     <list-item>
//       text
//       <bulleted-list>
//         <list-item>nested</list-item>
//       </bulleted-list>
//     </list-item>
//   </bulleted-list>
// Plate v53's list model is indent-based: SIBLING <p> blocks each carrying
// `listStyleType` + `indent`. The nested list-item becomes a SIBLING of its
// parent <p>, with `indent` incremented by one.
//
// CRITICAL: the old impl emitted nested list-items as CHILDREN of the
// outer <p>. BlockList then wrapped the outer <p> in <ul><li>...</li></ul>
// AND each child <p> in its own <ul><li>...</li></ul> — producing two
// stacked bullets per line. The flatten-to-siblings shape is what
// indent-based lists actually expect.
const convertList = (node: any, listStyleType: 'disc' | 'decimal', indent: number = 1): any[] => {
    const items = Array.isArray(node.children) ? node.children : [];
    const result: any[] = [];
    for (const item of items) {
        if (!item || typeof item !== 'object') continue;
        const align = pickAlign(item);
        // Partition the list-item's children:
        //  - inlineChildren: text/inline content that belongs to THIS bullet
        //  - nestedSiblings: <p> blocks coming from nested <bulleted-list> /
        //    <numbered-list> — these become siblings of THIS bullet at
        //    indent+1 (recursively, deeper nests bump further)
        const inlineChildren: any[] = [];
        const nestedSiblings: any[] = [];
        for (const child of Array.isArray(item.children) ? item.children : []) {
            if (child && (child.type === 'bulleted-list' || child.type === 'ul')) {
                nestedSiblings.push(...convertList(child, 'disc', indent + 1));
                continue;
            }
            if (child && (child.type === 'numbered-list' || child.type === 'ol')) {
                nestedSiblings.push(...convertList(child, 'decimal', indent + 1));
                continue;
            }
            // Anything else — convert via the standard pipeline. If the
            // result is a <p>, splat its children inline (the list-item's
            // own paragraph wrapper is implicit).
            const converted = convertNode(child);
            if (!converted) continue;
            const flatten = Array.isArray(converted) ? converted : [converted];
            for (const c of flatten) {
                if (c && c.type === 'p') inlineChildren.push(...(c.children ?? []));
                else inlineChildren.push(c);
            }
        }
        const block: any = {
            type: 'p',
            listStyleType,
            indent,
            children: inlineChildren.length ? inlineChildren : [{ text: '' }],
        };
        if (align) block.align = align;
        result.push(block);
        // Nested list items emitted as siblings (NOT children) — this is what
        // produces the indent-based shape Plate expects and prevents the
        // double-bullet bug.
        result.push(...nestedSiblings);
    }
    return result;
};

export function convertNode(node: any): any {
    if (!node || typeof node !== 'object') return null;

    // Text node — return cleaned text only
    if (isTextNode(node)) return cleanTextNode(node);

    const type = node.type as string | undefined;
    if (!type) {
        // No type — treat as text container, wrap children in paragraph
        return wrapAsParagraph(convertChildren(node.children || []));
    }

    const align = pickAlign(node);
    const withAlign = (block: any) => (align ? { ...block, align } : block);

    // Headings
    if (HEADING_MAP[type]) {
        return withAlign({ type: HEADING_MAP[type], children: convertChildren(node.children || []) });
    }

    switch (type) {
        case 'paragraph':
        case 'p':
            return withAlign({ type: 'p', children: convertChildren(node.children || []) });

        case 'table':
        case 'tableWithoutHeaders': {
            // Preserve colSizes: setTableColSize writes per-column widths to
            // this array; TableElement renders <colgroup><col> from it.
            // Without round-trip, autosave wipes the widths on every commit.
            const table: any = { type: 'table', children: convertChildren(node.children || []) };
            if (Array.isArray(node.colSizes)) table.colSizes = node.colSizes;
            return table;
        }

        case 'table-row':
        case 'tr': {
            const row: any = { type: 'tr', children: convertChildren(node.children || []) };
            if (typeof node.height === 'number') row.height = node.height;
            return row;
        }

        case 'table-cell':
        case 'td': {
            const children = ensureBlockChildren(convertChildren(node.children || []));
            const cell: any = { type: 'td', children };
            if (node.backgroundColor) cell.backgroundColor = node.backgroundColor;
            if (node.verticalAlign) cell.verticalAlign = node.verticalAlign;
            return cell;
        }

        case 'table-header':
        case 'th': {
            const children = ensureBlockChildren(convertChildren(node.children || []));
            const cell: any = { type: 'th', children };
            if (node.backgroundColor) cell.backgroundColor = node.backgroundColor;
            if (node.verticalAlign) cell.verticalAlign = node.verticalAlign;
            return cell;
        }

        case 'unorderedList':
        case 'bulleted-list':
            return convertList(node, 'disc');

        case 'orderedList':
        case 'numbered-list':
            return convertList(node, 'decimal');

        case 'list-item':
            // Standalone list-item (outside a list) — render as paragraph
            return { type: 'p', children: convertChildren(node.children || []) };

        case 'blockquote':
            return { type: 'blockquote', children: convertChildren(node.children || []) };

        case 'link':
        case 'a': {
            // Inline link element — LinkPlugin is registered and LinkElement
            // reads `element.url`. Preserve the href (legacy Slate stores it on
            // `href`, occasionally `url`); an href-less link keeps its text
            // inline rather than dropping to a bogus block paragraph.
            const url = node.url ?? node.href;
            const children = convertChildren(node.children || []);
            return url ? { type: 'a', url, children } : children;
        }

        case 'image': {
            // Preserve as a Plate image node (void element). URL may be a
            // base64 data URL, a numeric docId, or a presigned S3 URL —
            // imageUploadService manages the lifecycle. Children must be
            // an empty text node per slate's void-element contract.
            //
            // Note: `_uploading` / `_uploadError` are transient runtime props
            // (lazy upload state) and are intentionally NOT persisted — they
            // appear in editor state only, never in the saved Slate value.
            const img: any = { type: 'image', url: node.url ?? '', children: [{ text: '' }] };
            if (typeof node.width === 'number') img.width = node.width;
            const imgAlign = pickAlign(node);
            if (imgAlign) img.align = imgAlign;
            // Round-trip imageWrap. Omit when value matches the default
            // ('block-center') so legacy docs don't grow a new field on
            // every save.
            const wrap = (node as any).imageWrap;
            if (wrap === 'inline' || wrap === 'float-left' || wrap === 'float-right') img.imageWrap = wrap;
            // Alt text for a11y + DOCX/PDF semantic fidelity. Only emit when
            // non-empty so legacy docs don't grow a new field on every save.
            if (typeof (node as any).alt === 'string' && (node as any).alt.length > 0) img.alt = (node as any).alt;
            return img;
        }

        case 'column_group': {
            // Plate-only structural container — passthrough. Each direct
            // child must be a `column` per @platejs/layout's normalizer;
            // the withColumn normalizer will repair anything off-shape.
            const children = Array.isArray(node.children) ? node.children.map(convertNode).filter(Boolean) : [];
            return { type: 'column_group', children };
        }

        case 'column': {
            const col: any = { type: 'column', children: convertChildren(node.children || []) };
            if (typeof node.width === 'string') col.width = node.width;
            if (typeof node.id === 'string') col.id = node.id;
            return col;
        }

        default:
            // Unknown block — best-effort paragraph
            return wrapAsParagraph(convertChildren(node.children || []));
    }
}

const emptyParagraph = () => ({ type: 'p', children: [{ text: '' }] });

export function slateToPlateValue(value: any[]): any[] {
    if (!Array.isArray(value)) return [emptyParagraph()];
    const out: any[] = [];
    for (const node of value) {
        const converted = convertNode(node);
        if (Array.isArray(converted)) out.push(...converted);
        else if (converted) out.push(converted);
    }

    // Inject an empty paragraph between any two consecutive tables so the
    // user can click to place a caret between them. Plate has no built-in
    // affordance for cursor placement at a non-existent boundary between
    // two adjacent structured blocks.
    const spaced: any[] = [];
    for (let i = 0; i < out.length; i++) {
        spaced.push(out[i]);
        const cur = out[i];
        const next = out[i + 1];
        if (cur?.type === 'table' && next?.type === 'table') {
            spaced.push(emptyParagraph());
        }
    }

    return spaced.length ? spaced : [emptyParagraph()];
}
